import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import enTranslations from '../translations/en.json';
import kaTranslations from '../translations/ka.json';

const TranslationContext = createContext();

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};

// Detect user's country based on IP
const detectCountryFromIP = async () => {
  try {
    // Try ipapi.co first (free, no API key needed for basic usage)
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });
    
    if (response.ok) {
      const data = await response.json();
      return data.country_code || null;
    }
  } catch (error) {
    console.log('ipapi.co failed, trying ip-api.com:', error);
  }

  try {
    // Fallback to ip-api.com (use HTTPS)
    const response = await fetch('https://ip-api.com/json/', {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });
    
    if (response.ok) {
      const data = await response.json();
      return data.countryCode || null;
    }
  } catch (error) {
    console.log('IP detection failed:', error);
  }

  return null;
};

export const TranslationProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Get language from localStorage if set
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      return savedLanguage;
    }
    // Default to English, will be updated after IP detection
    return 'en';
  });

  const [isDetectingLocation, setIsDetectingLocation] = useState(true);

  // Detect country on mount and set language accordingly
  useEffect(() => {
    const detectAndSetLanguage = async () => {
      // Only detect if language hasn't been manually set
      const savedLanguage = localStorage.getItem('language');
      if (savedLanguage) {
        setIsDetectingLocation(false);
        return;
      }

      try {
        const countryCode = await detectCountryFromIP();
        if (countryCode === 'GE') {
          // Georgia - set to Georgian
          setLanguage('ka');
          localStorage.setItem('language', 'ka');
        } else {
          // Any other country - set to English
          setLanguage('en');
          localStorage.setItem('language', 'en');
        }
      } catch (error) {
        console.error('Error detecting location:', error);
        // Default to English on error
        setLanguage('en');
        localStorage.setItem('language', 'en');
      } finally {
        setIsDetectingLocation(false);
      }
    };

    detectAndSetLanguage();
  }, []);

  const translations = {
    en: enTranslations,
    ka: kaTranslations,
  };

  const t = useMemo(() => {
    return (key, params = {}) => {
      const keys = key.split('.');
      let value = translations[language];
      
      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          // Fallback to English if translation not found
          value = translations.en;
          for (const k2 of keys) {
            if (value && typeof value === 'object' && k2 in value) {
              value = value[k2];
            } else {
              return key; // Return key if translation not found
            }
          }
          break;
        }
      }

      // Replace parameters in the string
      if (typeof value === 'string' && Object.keys(params).length > 0) {
        return value.replace(/\{(\w+)\}/g, (match, paramKey) => {
          return params[paramKey] !== undefined ? params[paramKey] : match;
        });
      }

      return typeof value === 'string' ? value : key;
    };
  }, [language]);

  const changeLanguage = useMemo(() => {
    return (lang) => {
      setLanguage(lang);
      localStorage.setItem('language', lang);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('language', language);
    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      root.lang = language === 'ka' ? 'ka' : 'en';
      root.classList.toggle('lang-ka', language === 'ka');
    }
  }, [language]);

  const contextValue = useMemo(() => ({
    t,
    language,
    changeLanguage,
    isDetectingLocation
  }), [t, language, changeLanguage, isDetectingLocation]);

  return (
    <TranslationContext.Provider value={contextValue}>
      {children}
    </TranslationContext.Provider>
  );
};


